import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, signal } from '@angular/core';
import {
  FormArray,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  CreateBoothApplicationDto,
  BoothApplicationService,
  EventService,
  BoothOptionTemplateDto,
  EventDto,
} from '@millionshow/api-client';
import {
  ToastService,
  ModalService,
  PageModalContainer,
} from '@millionshow/client-libs';
import { of } from 'rxjs';
import { rxResource, toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { UploadService } from '../../services/upload.service';
import { ApplicationStep } from '../../interfaces/booth-application.interface';
import { getDayOfWeek, toDate } from '../../utils/date-formatter';
import { ApplicationCompleteModal } from './modals/application-complete.modal';
import { injectQueryParams } from 'ngxtension/inject-query-params';
import { PolicyModal } from '../../modals/policy/policy.modal';
import { StepIndicatorComponent } from './components/step-indicator.component';
import { BoothInfoComponent } from './components/booth-info.component';
import { BoothCompanyComponent } from './components/booth-company.component';
import { BoothOptionsComponent } from './components/booth-options.component';
import { TermsAgreementComponent } from './components/terms-agreement.component';
import { BoothActionButtonsComponent } from './components/booth-action-buttons.component';

@Component({
  selector: 'app-participant-application-form',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    PageModalContainer,
    StepIndicatorComponent,
    BoothInfoComponent,
    BoothCompanyComponent,
    BoothOptionsComponent,
    TermsAgreementComponent,
    BoothActionButtonsComponent,
  ],
  templateUrl: './booth-application-form.page.html',
  styleUrls: ['./booth-application-form.page.css'],
})
export default class BoothApplicationFormPage {
  private readonly boothApplicationService = inject(BoothApplicationService);
  private readonly eventService = inject(EventService);
  private readonly toastService = inject(ToastService);
  private readonly router = inject(Router);
  private readonly uploadService = inject(UploadService);
  private readonly modalService = inject(ModalService);

  readonly queryEventId = injectQueryParams<string>('eventId');

  uploadHandler = this.uploadService.upload;
  deleteHandler = this.uploadService.delete;

  currentStep = signal<ApplicationStep>('info');
  allAgreed = signal<boolean>(false);
  isTermsAgreed = signal<boolean>(false);
  isPrivacyAgreed = signal<boolean>(false);
  today = new Date();

  steps: { key: ApplicationStep; label: string }[] = [
    { key: 'info', label: '참가신청 정보' },
    { key: 'company', label: '업체/담당자 정보' },
    { key: 'options', label: '부스/부대시설 신청' },
  ];

  form = new FormGroup({
    // 1단계
    eventId: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    inquiry: new FormControl<string>('', { nonNullable: true }),

    // 2단계
    brandName: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    companyName: new FormControl<string>('', { nonNullable: true }),
    ceoName: new FormControl<string>(''),
    businessNumber: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    address: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    detailAddress: new FormControl<string>('', { nonNullable: true }),
    homepage: new FormControl<string>(''),
    products: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    phone: new FormControl<string>(''),
    fax: new FormControl<string>(''),
    description: new FormControl<string>(''),
    managerName: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    managerDept: new FormControl<string>(''),
    managerTitle: new FormControl<string>(''),
    managerPhone: new FormControl<string>(''),
    managerMobile: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    managerEmail: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required, Validators.email],
    }),
    managerFax: new FormControl<string>(''),
    files: new FormControl<string[]>([], {
      nonNullable: true,
      validators: [Validators.required, Validators.minLength(1)],
    }),

    // 3단계
    boothOptions: new FormArray<
      FormGroup<{
        templateId: FormControl<string | null>;
        name: FormControl<string>;
        unitPrice: FormControl<number>;
        priceInquiry: FormControl<string | null>;
        unit: FormControl<string | null>;
        quantity: FormControl<number | null>;
        description: FormControl<string | null>;
      }>
    >([]),
  });

  readonly eventIdValueSignal = toSignal(
    this.form.controls.eventId.valueChanges
  );

  $events = rxResource({
    stream: () =>
      this.eventService.eventControllerFindAllV1({
        status: 'ONGOING',
        type: 'AGRITEX' as const,
      }),
  });

  events = computed(() => {
    const items = this.$events.value() || [];
    return [...items].sort(
      (a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime()
    );
  });

  $event = rxResource({
    stream: ({ params }) => {
      if (!params.eventId) {
        return of(undefined);
      }

      return this.eventService.eventControllerFindByIdV1({
        id: params.eventId,
      });
    },
    params: () => ({ eventId: this.eventIdValueSignal() }),
  });

  event = computed(() => {
    const eventData = this.$event.value() as EventDto | undefined;
    return eventData;
  });

  $availableTemplates = rxResource({
    stream: ({ params }) => {
      if (!params.eventId) return of([]);
      return this.boothApplicationService.boothApplicationControllerGetAvailableTemplatesV1(
        { eventId: params.eventId, sourceType: 'AGRITEX' }
      );
    },
    params: () => ({ eventId: this.eventIdValueSignal() }),
  });

  boothOptionTemplates = computed<BoothOptionTemplateDto[]>(
    () => this.$availableTemplates.value() || []
  );

  depositDeadline = computed(() => {
    const applicationDate = new Date();
    const deadline = new Date(applicationDate);
    deadline.setDate(deadline.getDate() + 3);

    const year = String(deadline.getFullYear()).slice(2);
    const month = deadline.getMonth() + 1;
    const day = deadline.getDate();
    const weekday = getDayOfWeek(deadline);

    return `${year}년 ${month}월 ${day}일(${weekday})까지 납부`;
  });

  balanceDeadline = computed(() => {
    const eventData = this.event();

    if (!eventData?.startAt) {
      return '행사 날짜 확인 필요';
    }

    const eventDate = toDate(eventData.startAt);
    const deadline = new Date(eventDate);
    deadline.setMonth(deadline.getMonth() - 1);

    const year = String(deadline.getFullYear()).slice(2);
    const month = deadline.getMonth() + 1;
    const day = deadline.getDate();
    const weekday = getDayOfWeek(deadline);

    return `${year}년 ${month}월 ${day}일(${weekday})까지 납부`;
  });

  constructor() {
    effect(() => {
      const eventId = this.queryEventId();
      if (eventId && !this.form.controls.eventId.value) {
        this.form.controls.eventId.setValue(eventId);
      }
    });

    effect(() => {
      const eventId = this.eventIdValueSignal();

      if (eventId) {
        this.boothOptionsArray.clear();
        this.initializeBoothOptionsFromTemplates();
      }
    });

    // 개별 동의 체크박스 변경 감지 -> 전체 동의 업데이트
    effect(() => {
      const terms = this.isTermsAgreed();
      const privacy = this.isPrivacyAgreed();

      if (terms && privacy) {
        this.allAgreed.set(true);
      } else if (this.allAgreed()) {
        this.allAgreed.set(false);
      }
    });
  }

  get boothOptionsArray() {
    return this.form.controls.boothOptions;
  }

  sanitizeText(value: string | null | undefined) {
    if (!value) return '-';

    const stripped = value.replace(/<[^>]+>/g, '').trim();

    return stripped ? value : '-';
  }

  initializeBoothOptionsFromTemplates(): void {
    const templates = this.boothOptionTemplates();
    const activeTemplates = templates
      .filter(t => t.isActive)
      .sort((a, b) => a.sortOrder - b.sortOrder);

    activeTemplates.forEach(template => {
      this.boothOptionsArray.push(
        new FormGroup({
          templateId: new FormControl<string | null>(template.id),
          name: new FormControl<string>(template.name, { nonNullable: true }),
          unitPrice: new FormControl<number>(0, {
            nonNullable: true,
          }),
          priceInquiry: new FormControl<string | null>(
            template.priceInquiry || null
          ),
          unit: new FormControl<string | null>(template.unit || null),
          quantity: new FormControl<number | null>(0),
          description: new FormControl<string | null>(
            template.description || null
          ),
        })
      );
    });
  }

  calculateTotal(): number {
    return this.boothOptionsArray.controls.reduce((sum, control) => {
      const unitPrice = control.value.unitPrice || 0;
      const quantity = control.value.quantity || 0;
      return sum + unitPrice * quantity;
    }, 0);
  }

  calculateDeposit(): number {
    return this.calculateTotal() * 0.5;
  }

  calculateBalance(): number {
    return this.calculateTotal() - this.calculateDeposit();
  }

  isStepValid(step: ApplicationStep): boolean {
    switch (step) {
      case 'info':
        return (
          this.form.controls.eventId.valid && this.form.controls.inquiry.valid
        );
      case 'company':
        return (
          this.form.controls.brandName.valid &&
          this.form.controls.businessNumber.valid &&
          this.form.controls.address.valid &&
          this.form.controls.products.valid &&
          this.form.controls.files.valid &&
          this.form.controls.managerName.valid &&
          this.form.controls.managerMobile.valid &&
          this.form.controls.managerEmail.valid
        );
      case 'options':
        return true;
      default:
        return false;
    }
  }

  goToStep(step: ApplicationStep): void {
    const currentIndex = this.steps.findIndex(
      s => s.key === this.currentStep()
    );
    const targetIndex = this.steps.findIndex(s => s.key === step);

    if (targetIndex > currentIndex) {
      if (!this.isStepValid(this.currentStep())) {
        this.toastService.error('필수 항목을 입력해주세요.');
        return;
      }
    }

    this.currentStep.set(step);
  }

  nextStep(): void {
    if (!this.isStepValid(this.currentStep())) {
      this.toastService.error('필수 항목을 입력해주세요.');
      return;
    }

    const currentIndex = this.steps.findIndex(
      s => s.key === this.currentStep()
    );
    if (currentIndex < this.steps.length - 1) {
      this.currentStep.set(this.steps[currentIndex + 1].key);
    }
  }

  prevStep(): void {
    const currentIndex = this.steps.findIndex(
      s => s.key === this.currentStep()
    );
    if (currentIndex > 0) {
      this.currentStep.set(this.steps[currentIndex - 1].key);
    }
  }

  increaseQuantity(control: FormGroup): void {
    const currentValue = control.value.quantity || 0;
    control.patchValue({ quantity: currentValue + 1 });
  }

  decreaseQuantity(control: FormGroup): void {
    const currentValue = control.value.quantity || 0;
    if (currentValue > 0) {
      control.patchValue({ quantity: currentValue - 1 });
    }
  }

  isStepCompleted(stepIndex: number): boolean {
    const currentIndex = this.steps.findIndex(
      s => s.key === this.currentStep()
    );
    return currentIndex > stepIndex;
  }

  isStepActive(stepKey: ApplicationStep): boolean {
    return this.currentStep() === stepKey;
  }

  toggleAllAgreed(checked: boolean): void {
    this.allAgreed.set(checked);
    this.isTermsAgreed.set(checked);
    this.isPrivacyAgreed.set(checked);
  }

  canSubmit(): boolean {
    return this.form.valid && this.allAgreed();
  }

  submit(): void {
    if (!this.canSubmit()) {
      if (!this.allAgreed()) {
        this.toastService.error('이용약관 및 개인정보 이용에 동의해주세요.');
      } else {
        this.toastService.error('필수 항목을 입력해주세요.');
      }
      return;
    }

    const formValue = this.form.getRawValue();

    if (!formValue.files || formValue.files.length === 0) {
      this.toastService.error('사업자등록증을 업로드해주세요.');
      return;
    }

    // 기본 주소와 상세 주소 합치기
    const fullAddress = formValue.detailAddress
      ? `${formValue.address}, ${formValue.detailAddress}`
      : formValue.address;

    const companyData: any = {
      brandName: formValue.brandName,
      companyName: formValue.companyName,
      ceoName: formValue.ceoName || undefined,
      businessNumber: formValue.businessNumber.replace(/[^0-9]/g, ''),
      address: fullAddress,
      homepage: formValue.homepage || undefined,
      products: formValue.products || undefined,
      phone: formValue.phone
        ? formValue.phone.replace(/[^0-9]/g, '')
        : undefined,
      fax: formValue.fax ? formValue.fax.replace(/[^0-9]/g, '') : undefined,
      description: formValue.description || undefined,
      managerName: formValue.managerName,
      managerDept: formValue.managerDept || undefined,
      managerTitle: formValue.managerTitle || undefined,
      managerPhone: formValue.managerPhone
        ? formValue.managerPhone.replace(/[^0-9]/g, '')
        : undefined,
      managerMobile: formValue.managerMobile.replace(/[^0-9]/g, ''),
      managerEmail: formValue.managerEmail,
      managerFax: formValue.managerFax
        ? formValue.managerFax.replace(/[^0-9]/g, '')
        : undefined,
      files: formValue?.files ?? [],
      isExposed: true,
    };

    const body: CreateBoothApplicationDto = {
      eventId: formValue.eventId,
      companyId: undefined,
      companyData: companyData,
      inquiry: formValue.inquiry || undefined,
      sourceType: 'AGRITEX',
      boothOptions: formValue.boothOptions
        .filter(option => (option.quantity || 0) > 0)
        .map(option => ({
          templateId: option.templateId || undefined,
          name: option.name,
          unitPrice: 0,
          unit: option.unit || undefined,
          quantity: option.quantity || 0,
        })),
    };

    this.boothApplicationService
      .boothApplicationControllerCreateV1({ body })
      .subscribe({
        next: () => {
          const modal = this.modalService.create(ApplicationCompleteModal, {
            componentProps: {
              eventTitle: this.event()?.title || '',
              companyName: formValue.companyName || '',
              managerName: formValue.managerName,
              managerPhone: formValue.managerMobile,
            },
          });
          modal.closed.subscribe(() => {
            this.router.navigate(['/']);
          });
        },
        error: err => {
          const message =
            err?.error?.message ||
            err?.message ||
            '신청 중 오류가 발생했습니다.';
          this.toastService.error({ message });
        },
      });
  }

  openPolicyModal(type: 'use' | 'privacy' | 'marketing'): void {
    this.modalService.create(PolicyModal, {
      componentProps: { type },
    });
  }
}
